import clsx from "clsx";
import {
  type ExtractDocsContextValue,
  getFileForSourceDocument,
  useExtractDocs,
} from "context/ExtractDocsContext";
import {
  CONTRADICTION_STATUS,
  DISCHARGE_LETTER_FORM_NAMES,
  DOCS_FORM_NAMES,
  REHAB_FORM_NAMES,
  FIELD_CREATION_TYPE,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_SUCCEED,
  SOURCE_DOCUMENT_SOURCES,
} from "core/consts";
import { isVoiceSourceDocumentSource } from "core/model/files/utils";
import { activateDocsFeedback } from "core/model/utils/featureFlags";
import {
  AuctionForm,
  Contradictions,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormSourceDocument,
  DocsFormStatusResponse,
  EncryptedField,
  FieldCreationType,
  FileState,
  FormMetadata,
  QueryProgress,
  RehabFormName,
  SourceFields,
  ValueOf,
} from "core/types";
import {
  Button,
  ButtonVariant,
  ControlledCheckboxGroup,
  ControlledCheckboxGroupProps,
  ControlledDatePickerProps,
  ControlledRadioGroup,
  ControlledRadioGroupProps,
  DatePicker,
  Tooltip,
} from "ds/ui";
import { useCopy } from "dsl/atoms/CopyButton/useCopy";
import { useCreateFileUrl } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import {
  AlertTriangleIcon,
  CheckIcon,
  CopyIcon,
  FileSearch2Icon,
} from "lucide-react";
import { resolveArhVersion } from "onprem/resolveArhVersion";
import { Dispatch, ReactNode, SetStateAction, useCallback } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { useFormContext, useWatch } from "react-hook-form";
// eslint-disable-next-line no-react-hook-form-outside-ds-ui/no-react-hook-form-outside-ds-ui -- TODO: remove this once we have a way to focus the components on validation - https://recare.atlassian.net/browse/DEV-19781
import { useController } from "react-hook-form";
import { useTranslations } from "translations";
import { FORM_NAME_TO_FILE_TYPE } from "./formNameToFileType";
import { SourceInformation } from "./useDocsForm";

export { FORM_NAME_TO_FILE_TYPE };

export type MigratedForms = (typeof DISCHARGE_LETTER_FORM_NAMES)[number];

export const REFRESH_MODE = {
  ALL_FIELDS: "refresh-all-fields",
  UNEDITED_FIELDS: "refresh-unedited-fields",
} as const;

export type RefreshMode = ValueOf<typeof REFRESH_MODE>;

export function isDischargeLetterFormName(
  formName: DocsFormName,
): formName is MigratedForms {
  return (DISCHARGE_LETTER_FORM_NAMES as readonly DocsFormName[]).includes(
    formName,
  );
}

export function isRehabFormName(
  formName: DocsFormName,
): formName is RehabFormName {
  return (Object.values(REHAB_FORM_NAMES) as readonly DocsFormName[]).includes(
    formName,
  );
}

const COPYABLE_FORMS: DocsFormName[] = [...DISCHARGE_LETTER_FORM_NAMES];

const CopyButton = <T extends DocsFormName = DocsFormName>({
  fieldName,
}: {
  fieldName: keyof DocsFormDataMapped<T>;
}) => {
  const fieldValue = useWatch({
    name: fieldName as string,
  });
  const translations = useTranslations();
  const { copyMessage, copyStatus, copyToClipboard } = useCopy({
    onCopyMessage: "todo: translation",
  });

  const renderContent = () => (
    <Button
      aria-label={translations.chat.copyMessage}
      color="primary"
      id={`copy-button-${String(fieldName)}`}
      isDisabled={!fieldValue}
      isIconOnly
      startContent={
        copyStatus === QUERY_PROGRESS_SUCCEED ? <CheckIcon /> : <CopyIcon />
      }
      trackEventProps={{
        field_name: String(fieldName),
        id: `copy-button`,
      }}
      variant="ghost"
      onPress={() => copyToClipboard(fieldValue)}
    />
  );

  if (copyStatus === QUERY_PROGRESS_SUCCEED) {
    return (
      <Tooltip content={copyMessage} isOpen placement="bottom">
        {renderContent()}
      </Tooltip>
    );
  }

  return renderContent();
};

const SourceButton = ({
  fieldName,
  isEdited,
  isIconOnly,
  isSourceFieldsConflict,
  onPress,
}: {
  fieldName: string;
  isSourceFieldsConflict: boolean;
  isEdited: boolean;
  isIconOnly: boolean;
  onPress: () => void;
}) => {
  const translations = useTranslations();
  const buttonId = `${isEdited ? "reference" : "source"}-button`;
  const buttonLabel =
    translations.extractDocs.splitView.sourceInformation.buttonSource;
  const buttonText = isEdited
    ? translations.extractDocs.splitView.sourceInformation.buttonReference
    : translations.extractDocs.splitView.sourceInformation.buttonSource;

  const sharedProps = {
    "aria-label": buttonLabel,
    className: "whitespace-nowrap",
    color: isSourceFieldsConflict ? ("danger" as const) : ("primary" as const),
    endContent: isSourceFieldsConflict ? (
      <AlertTriangleIcon />
    ) : (
      <FileSearch2Icon />
    ),
    id: `${buttonId}-${fieldName}`,
    onPress,
    trackEventProps: {
      field_name: fieldName,
      id: buttonId,
    },
    variant: isEdited ? "light" : ("ghost" as ButtonVariant),
  };

  if (isIconOnly) {
    return <Button {...sharedProps} isIconOnly />;
  }

  return <Button {...sharedProps}>{buttonText}</Button>;
};

export const SourceWrapper = <T extends DocsFormName = DocsFormName>({
  children,
  fieldName,
  formName,
  hideSource,
  isIconOnly = false,
  sourceInformation,
}: {
  children: ReactNode;
  sourceInformation: SourceInformation<T>;
  fieldName: keyof DocsFormDataMapped<T>;
  isIconOnly?: boolean;
  hideSource?: boolean;
  formName: T;
}) => {
  if (!children) {
    return null;
  }

  if (hideSource) {
    return <div className="p-1">{children}</div>;
  }

  const isSelected = sourceInformation.isSourceSelected(fieldName);
  const sourceFieldMetadata = sourceInformation.getSourceFields(fieldName);
  const isEdited = sourceFieldMetadata?.manually_edited ?? false;

  const canCopyResult = COPYABLE_FORMS.includes(formName);
  const sourceDocuments = sourceFieldMetadata?.source_documents ?? [];
  const hasSourceDocuments = sourceDocuments.length > 0;
  const contradictions = sourceInformation.getContradictionFields(fieldName);

  const isSourceFieldsConflict =
    contradictions?.status === CONTRADICTION_STATUS.CONFLICT &&
    (contradictions?.candidates?.length ?? 0) > 0;

  return (
    <div
      className={clsx("flex w-full gap-2 rounded-sm px-4 tablet:py-2", {
        "bg-blue-50": isSelected,
        "bg-white": !isSelected,
      })}
    >
      <div
        className={clsx("min-w-0", {
          "basis-[70%]": !isIconOnly,
          "basis-[85%]": isIconOnly,
        })}
      >
        {children}
      </div>
      <div className="flex min-w-0 flex-1 flex-row flex-wrap items-end justify-end gap-2">
        {canCopyResult && !activateDocsFeedback && (
          <CopyButton fieldName={fieldName} />
        )}
        {hasSourceDocuments ? (
          <SourceButton
            fieldName={String(fieldName)}
            isEdited={isEdited}
            isIconOnly={isIconOnly}
            isSourceFieldsConflict={isSourceFieldsConflict}
            onPress={() => sourceInformation.handleSetSelectedSource(fieldName)}
          />
        ) : null}
      </div>
    </div>
  );
};

export function getFieldCreationType(
  inferredValue: unknown,
  value: unknown,
  sourceField: SourceFields | undefined,
): FieldCreationType {
  if (inferredValue != null && value != null) {
    return FIELD_CREATION_TYPE.INFERENCE;
  }
  if (sourceField?.creation_type) {
    return sourceField.creation_type;
  }
  return FIELD_CREATION_TYPE.USER;
}

export function getManuallyEdited(
  inferredValue: unknown,
  value: unknown,
  previousFieldValue: unknown,
  sourceField: SourceFields | undefined,
): boolean {
  if (inferredValue != null) {
    return value !== inferredValue;
  }
  if (previousFieldValue != null) {
    return previousFieldValue !== value || !!sourceField?.manually_edited;
  }
  return false;
}

export function getManuallyEditedFieldKeys<T extends DocsFormName>({
  currentValues,
  previousFillResult,
  previousFormData,
  savedMetadata,
}: {
  currentValues: Partial<DocsFormDataMapped<T>>;
  previousFillResult: DocsFormStatusResponse | null;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
  savedMetadata: FormMetadata<T> | undefined;
}): Set<string> {
  const editedKeys = new Set<string>();
  Object.keys(currentValues).forEach((key) => {
    const value = currentValues[key as keyof DocsFormDataMapped<T>];
    const inferredValue = previousFillResult?.form?.[key]?.value;
    const previousFieldValue =
      previousFormData?.[key as keyof DocsFormDataMapped<T>];
    const sourceField =
      savedMetadata?.source_fields?.[key as keyof DocsFormDataMapped<T>];
    if (
      getManuallyEdited(inferredValue, value, previousFieldValue, sourceField)
    ) {
      editedKeys.add(key);
    }
  });
  return editedKeys;
}

export function getDocsFormIsFilled(
  hasPreviousFormValue: boolean,
  importFormProgress: QueryProgress,
): boolean {
  return (
    hasPreviousFormValue || importFormProgress !== QUERY_PROGRESS_NOT_STARTED
  );
}

export function mergeSourceFieldMetadata({
  inferredValue,
  isInOverwriteWarning,
  savedFieldMetadata,
}: {
  inferredValue: {
    value: string | null;
    source_documents: DocsFormSourceDocument[] | undefined;
  };
  isInOverwriteWarning: boolean;
  savedFieldMetadata: SourceFields | undefined;
}): SourceFields {
  const hasNewInference =
    inferredValue.value != null && inferredValue.value !== "";
  return {
    ...(savedFieldMetadata ?? ({} as SourceFields)),
    manually_edited:
      hasNewInference && !isInOverwriteWarning
        ? false
        : !!savedFieldMetadata?.manually_edited,
    source_documents: hasNewInference
      ? inferredValue.source_documents ?? []
      : savedFieldMetadata?.source_documents ?? [],
  };
}

export function applyFeedbackOverridesToSourceFields<T extends DocsFormName>({
  base,
  feedbackOverrides,
}: {
  base: FormMetadata<T>["source_fields"];
  feedbackOverrides: Record<
    string,
    Pick<SourceFields, "rating" | "rating_description">
  >;
}): FormMetadata<T>["source_fields"] {
  if (Object.keys(feedbackOverrides).length === 0) return base;

  const merged = { ...base } as Record<string, SourceFields | undefined>;

  for (const [key, override] of Object.entries(feedbackOverrides)) {
    const value = merged[key];
    merged[key] = {
      creation_type: value?.creation_type ?? FIELD_CREATION_TYPE.USER,
      manually_edited: value?.manually_edited ?? false,
      source_documents: value?.source_documents ?? [],
      ...(value ?? {}),
      ...(override.rating !== undefined ? { rating: override.rating } : {}),
      ...(override.rating_description !== undefined
        ? { rating_description: override.rating_description }
        : {}),
    };
  }

  return merged as FormMetadata<T>["source_fields"];
}

export function buildFormMetadata<T extends DocsFormName>({
  docsFillFormResult,
  formData,
  previousFormData,
  sourceInformation,
}: {
  formData: DocsFormDataMapped<T>;
  sourceInformation: SourceInformation<T>;
  docsFillFormResult: DocsFormStatusResponse | null;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
}): FormMetadata<T> {
  const sourceFields = Object.entries(formData).reduce(
    (acc, [key, value]) => {
      const inferredValue = docsFillFormResult?.form[key]?.value;
      const sourceField = sourceInformation.getSourceFields(
        key as keyof DocsFormDataMapped<T>,
      );
      const sourceDocuments = sourceField?.source_documents ?? [];
      const previousFieldValue =
        previousFormData?.[key as keyof DocsFormDataMapped<T>];

      return {
        ...acc,
        [key]: {
          creation_type: getFieldCreationType(
            inferredValue,
            value,
            sourceField,
          ),
          manually_edited: getManuallyEdited(
            inferredValue,
            value,
            previousFieldValue,
            sourceField,
          ),
          ...(sourceField?.rating != null
            ? { rating: sourceField.rating }
            : {}),
          ...(sourceField?.rating_description != null
            ? { rating_description: sourceField.rating_description }
            : {}),
          source_documents: sourceDocuments,
        },
      };
    },
    {} as FormMetadata<T>["source_fields"],
  );

  // Contradictions are stored at form level (encrypted), not per field in source_fields.
  const contradictions = Object.entries(formData).reduce<
    Record<string, Contradictions>
  >((acc, [key]) => {
    const fieldContradictions = sourceInformation.getContradictionFields(
      key as keyof DocsFormDataMapped<T>,
    );

    if (fieldContradictions == null) return acc;
    const value =
      typeof fieldContradictions === "object" &&
      "decrypted" in fieldContradictions
        ? (fieldContradictions as { decrypted?: string }).decrypted
        : fieldContradictions;
    let parsed: Contradictions | null;
    if (typeof value === "object" && value !== null && "status" in value) {
      parsed = value;
    } else if (typeof value === "string") {
      try {
        parsed = JSON.parse(value) as Contradictions;
      } catch {
        parsed = null;
      }
    } else {
      parsed = null;
    }
    if (parsed?.status != null) acc[key] = parsed;
    return acc;
  }, {});

  return {
    contradictions: (convertEncryptedFieldOut(JSON.stringify(contradictions)) ??
      null) as EncryptedField | null,
    source_fields: sourceFields,
  };
}

export function buildSaveFormMetadata<T extends DocsFormName>({
  auctionForms,
  docsFillFormResult,
  formData,
  formName,
  includeAuctionSchemaVersion = false,
  previousFormData,
  sourceInformation,
}: {
  auctionForms?: AuctionForm[] | null;
  docsFillFormResult: DocsFormStatusResponse | null;
  formData: DocsFormDataMapped<T>;
  formName: T;
  includeAuctionSchemaVersion?: boolean;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
  sourceInformation: SourceInformation<T>;
}): FormMetadata<T> {
  const metadata: FormMetadata<T> = {
    ...buildFormMetadata({
      docsFillFormResult,
      formData,
      previousFormData,
      sourceInformation,
    }),
    // Any form edit done not in the doctor approval page resets the approval status.
    is_doctor_approved: false,
  };

  const isGkvForm =
    formName === DOCS_FORM_NAMES.GKV_GENERAL ||
    formName === DOCS_FORM_NAMES.GKV_MEDICAL;

  if (!includeAuctionSchemaVersion || !isGkvForm) {
    return metadata;
  }

  return {
    ...metadata,
    schema_version: resolveArhVersion(auctionForms),
  };
}

export type FormSaveTrackingStats = {
  aiFieldsAccepted: string[];
  aiFieldsCleared: string[];
  aiFieldsEdited: string[];
  manualFieldsAdded: string[];
};

export function buildFormSaveTrackingStats<T extends DocsFormName>({
  docsFillFormResult,
  formData,
  metadata,
}: {
  docsFillFormResult: DocsFormStatusResponse | null;
  formData: DocsFormDataMapped<T>;
  metadata: FormMetadata<T>;
}): FormSaveTrackingStats {
  const aiFieldsAccepted: string[] = [];
  const aiFieldsCleared: string[] = [];
  const aiFieldsEdited: string[] = [];
  const manualFieldsAdded: string[] = [];

  for (const [field, value] of Object.entries(formData)) {
    const sourceField = metadata.source_fields[
      field as keyof DocsFormDataMapped<T>
    ] as SourceFields | undefined;
    const inferredValue = docsFillFormResult?.form[field]?.value;
    const isEmpty = value == null || value === "";

    if (inferredValue != null) {
      if (isEmpty) {
        aiFieldsCleared.push(field);
      } else if (sourceField?.creation_type === FIELD_CREATION_TYPE.INFERENCE) {
        if (sourceField.manually_edited) {
          aiFieldsEdited.push(field);
        } else {
          aiFieldsAccepted.push(field);
        }
      }
    } else if (!isEmpty) {
      manualFieldsAdded.push(field);
    }
  }

  return {
    aiFieldsAccepted,
    aiFieldsCleared,
    aiFieldsEdited,
    manualFieldsAdded,
  };
}

function buildSelectedSourceDocument(
  sourceDocument: DocsFormSourceDocument,
  patch: {
    filename?: string;
    uploadedAt: number | undefined;
    url: string;
    is_deleted?: boolean;
  },
): DocsFormSourceDocument {
  return {
    ...sourceDocument,
    filename: patch.filename ?? sourceDocument.filename,
    is_deleted: patch.is_deleted ?? sourceDocument.is_deleted,
    uploaded_at: patch.uploadedAt,
    url: patch.url,
  };
}

type SourceDocumentFileListEntry = { name: string; file: FileState };

async function buildSelectedSourceDocumentForFileEntry(
  sourceDocument: DocsFormSourceDocument,
  entry: SourceDocumentFileListEntry | undefined,
  urlCache: Map<string, string>,
  canUseExtractedDocument: boolean | undefined,
  utils: ExtractDocsContextValue["utils"],
  createFileUrl: (file: FileState) => Promise<string | undefined>,
): Promise<DocsFormSourceDocument | undefined> {
  if (!entry) {
    return buildSelectedSourceDocument(sourceDocument, {
      is_deleted: true,
      uploadedAt: undefined,
      url: "",
    });
  }

  const fromEntry = (url: string) =>
    buildSelectedSourceDocument(sourceDocument, {
      filename: entry.name,
      uploadedAt: entry.file.updated_at!,
      url,
    });

  const cacheKey = String(sourceDocument.id);
  const cachedUrl = urlCache.get(cacheKey);
  if (cachedUrl) {
    return fromEntry(cachedUrl);
  }

  if (canUseExtractedDocument) {
    const extractedDocument = utils.getExtractedDocument(entry.file);
    if (extractedDocument?.source_document_uri) {
      return fromEntry(extractedDocument.source_document_uri);
    }
  }

  const url = await createFileUrl(entry.file);
  if (url) {
    urlCache.set(cacheKey, url);
    return fromEntry(url);
  }

  return undefined;
}

export function useSelectSourceDocumentFile({
  canUseExtractedDocument,
  externalId,
  onComplete,
  setProgress,
}: {
  externalId: string;
  onComplete?: () => void;
  setProgress?: Dispatch<SetStateAction<QueryProgress>>;
  canUseExtractedDocument?: boolean;
}) {
  const { extractOcrStatus, utils } = useExtractDocs();
  const createFileUrl = useCreateFileUrl({
    externalId,
    onComplete,
    setProgress,
  });

  return useCallback(
    async ({
      files,
      sourceDocument,
      urlCache,
    }: {
      sourceDocument: DocsFormSourceDocument;
      files: { name: string; file: FileState }[];
      urlCache: Map<string, string>;
    }): Promise<DocsFormSourceDocument | undefined> => {
      if (isVoiceSourceDocumentSource(sourceDocument.source)) {
        // Voice transcripts are AI-core-only documents; their content lives in
        // the processed document (there is no source_document_uri).
        const extractedDocument = extractOcrStatus.result?.documents?.find(
          (doc) => String(doc.document_id) === String(sourceDocument.id),
        );

        return buildSelectedSourceDocument(sourceDocument, {
          filename: extractedDocument?.filename ?? sourceDocument.filename,
          uploadedAt:
            extractedDocument?.uploaded_at ?? sourceDocument.uploaded_at ?? 0,
          url:
            extractedDocument?.processed_document_uri ??
            sourceDocument.source_document_uri ??
            "",
        });
      }

      if (sourceDocument.source === SOURCE_DOCUMENT_SOURCES.KIS_DATA) {
        const extractedDocument = utils.getExtractedDocument({
          id: sourceDocument.id,
        } as FileState);

        if (extractedDocument?.source_document_uri) {
          return buildSelectedSourceDocument(sourceDocument, {
            filename: extractedDocument.filename,
            uploadedAt: extractedDocument.uploaded_at ?? 0,
            url: extractedDocument.source_document_uri,
          });
        }

        return buildSelectedSourceDocument(sourceDocument, {
          is_deleted: true,
          uploadedAt: undefined,
          url: "",
        });
      }

      if (sourceDocument.source === SOURCE_DOCUMENT_SOURCES.KIS_FILE) {
        if (sourceDocument.url) {
          return buildSelectedSourceDocument(sourceDocument, {
            uploadedAt: sourceDocument.uploaded_at ?? 0,
            url: sourceDocument.url,
          });
        }

        const extractedDocument = extractOcrStatus.result?.documents?.find(
          (doc) => doc.kis_file_id === sourceDocument.kis_file_id,
        );

        return buildSelectedSourceDocument(sourceDocument, {
          filename: extractedDocument?.filename ?? sourceDocument.filename,
          uploadedAt:
            extractedDocument?.uploaded_at ?? sourceDocument.uploaded_at ?? 0,
          url: extractedDocument?.source_document_uri ?? "",
        });
      }

      if (sourceDocument.source === SOURCE_DOCUMENT_SOURCES.RECARE_DATA) {
        return buildSelectedSourceDocument(sourceDocument, {
          uploadedAt: undefined,
          url: "",
        });
      }

      if (sourceDocument.source === SOURCE_DOCUMENT_SOURCES.RECARE_FILE) {
        const entry = getFileForSourceDocument(files, sourceDocument);
        return buildSelectedSourceDocumentForFileEntry(
          sourceDocument,
          entry,
          urlCache,
          canUseExtractedDocument,
          utils,
          createFileUrl,
        );
      }

      return undefined;
    },
    [canUseExtractedDocument, createFileUrl, extractOcrStatus, utils],
  );
}

// TODO: remove these helper functions once we have a way to focus the components on validation
// Adds `tabIndex={-1}` and `div wth ref` so RHF `shouldFocusError` can focus non-native group roots.
// https://recare.atlassian.net/browse/DEV-19781
export function ControlledCheckboxGroupWithFocus<T extends string | number>(
  props: ControlledCheckboxGroupProps<T>,
) {
  return <ControlledCheckboxGroup {...props} tabIndex={-1} />;
}

export function ControlledRadioGroupWithFocus<T extends string | number>(
  props: ControlledRadioGroupProps<T>,
) {
  return <ControlledRadioGroup {...props} tabIndex={-1} />;
}

export function ControlledDatePickerWithFocus({
  name,
  ...props
}: ControlledDatePickerProps) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });
  const { ref, ...fieldRest } = field;

  return (
    <div ref={ref} className="outline-none focus:outline-none" tabIndex={-1}>
      <DatePicker
        {...props}
        {...fieldRest}
        {...fieldState}
        errorMessage={fieldState?.error?.message}
        isInvalid={!!fieldState?.error?.message}
        name={name}
        value={field.value}
        onChange={(value) => {
          field.onChange(value);
        }}
      />
    </div>
  );
}
